<template>
  <div>
    <div class="row mt-4">
      <div class="col-md-12 mb-2">
        <span class="back-btn" @click="$emit('setCurrentTab','GeneralInformationForExceptionRequest')">
          <font-awesome-icon icon="arrow-left" size="sm"/>
          Back to general information
        </span>
      </div>

      <div class="col">
        <div>
          <div class="display-tooltip">
              <span class="title required-field my-2 d-block">
                <span>7. </span>
                <header-with-tooltip :tooltip-text="approximateCostTooltip"><span class="mr-1">Approximate cost</span></header-with-tooltip>
              </span>
          </div>

          <div class="mb-4">
              <span class="currencyText">
                <input
                    type="number"
                    id="approximateCost"
                    class="form-control w-100 mt-1"
                    placeholder="Approximate Cost"
                    v-model="exceptionSubmissionRequestData.approximateSpend"
                    name="exceptionSubmissionRequestData.approximateSpend"
                    v-validate="'required|numeric'"
                    min="0"
                    autocomplete="off"
                    data-vv-validate-on="input"
                    @focus="testForValidInput"
                />
              </span>
            <span
                class="text-danger input-error-text"
                v-if="errors.has('exceptionSubmissionRequestData.approximateSpend')"
            >This field is required.</span
            >
          </div>
        </div>

        <div class="title">
          <span>8. </span>
          <header-with-tooltip
              class="required-field"
              :tooltip-text="CONSTANTS.SUPPLIER_NAME_TOOLTIP"
              :isHTML="true"
              :show-icon="true">
            <span class="mr-1">Supplier name</span>
          </header-with-tooltip>
          <br>
          <span class="font-italic">{{ supplierNameHelpText }}</span>
        </div>
        <div class="mb-4">
          <supplier-auto-complete
              :isRequired="true"
              name="exceptionSubmissionRequestData.supplierName"
              :currentSelection="exceptionSubmissionRequestData.supplierName"
              @onItemSelected="assignSupplierName"
              :key="supplierAutoCompleteKey"
          />
        </div>

        <div class="title pt-2">
          <span>8 a. </span>
          <span>URL for supplier’s website</span>
        </div>
        <div class="mb-4 pb-4">
          <input
              class="form-control w-100 mt-1"
              v-validate="'url'"
              name="exceptionSubmissionRequestData.supplierWebsite"
              v-model.lazy="exceptionSubmissionRequestData.supplierWebsite"
              autocomplete="chrome-off"
          >
          <span
              class="text-danger input-error-text"
              v-if="errors.has('exceptionSubmissionRequestData.supplierWebsite')"
          >Website should be of a valid format</span>
        </div>

        <div class="row border-outline">
          <strong class="border-text">Supplier contact details</strong>

          <div class="col-12 col-md-6">
            <div class="title required-field pt-2">
              <span>8 b. </span>
              <header-with-tooltip
                  :tooltip-text="`Not entering correct supplier account representative information could delay ticket being completed.`"
                  :isHTML="true"
                  :show-icon="true">
                <span class="mr-1">Name of your contact at {{ supplierName }}</span>
              </header-with-tooltip>
            </div>
            <div class="mb-4">
              <input
                  class="form-control w-100 mt-1"
                  name="exceptionSubmissionRequestData.supplierContactName"
                  v-model="exceptionSubmissionRequestData.supplierContactName"
                  autocomplete="chrome-off"
                  v-validate="'required'"
                  data-vv-validate-on="input"
              >
              <span
                  class="text-danger input-error-text"
                  v-if="errors.has('exceptionSubmissionRequestData.supplierContactName')"
              >This field is required</span>
            </div>

          </div>

          <div class="col-12 col-md-6">
            <div class="title pt-2">
              <span>8 c. </span>
              <span>Supplier contact role</span>
            </div>
            <div class="item mb-4 select-style">
              <model-list-select
                  :list="supplierContactRoleOptions"
                  :optionValue="'value'"
                  :optionText="'text'"
                  name="exceptionSubmissionRequestData.supplierContactRole"
                  v-model="exceptionSubmissionRequestData.supplierContactRole"
                  autocomplete="chrome-off"
                  class="form-control w-100 mt-1"/>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="title required-field pt-2">
              <span>8 d. </span>
              <header-with-tooltip
                  :tooltip-text="`Not entering correct supplier account representative information could delay ticket being completed.`"
                  :isHTML="true"
                  :show-icon="true">
                <span class="mr-1">Supplier contact email</span>
              </header-with-tooltip>
            </div>
            <div class="mb-4">
              <input
                  class="form-control w-100 mt-1"
                  v-validate="'required|email'"
                  name="exceptionSubmissionRequestData.supplierContactEmail"
                  v-model.lazy="exceptionSubmissionRequestData.supplierContactEmail"
                  data-vv-validate-on="input"
                  autocomplete="chrome-off"
              >
              <span
                  class="text-danger input-error-text"
                  v-if="errors.has('exceptionSubmissionRequestData.supplierContactEmail')"
              >This field is required</span>
            </div>
          </div>

          <div class="col-12 col-md-6 mt-1">
            <div class="title required-field pt-2">
              <span>8 e. </span>
              <header-with-tooltip
                  :tooltip-text="`Enter the country where the supplier services will be provided from.`"
                  :isHTML="true"
                  :show-icon="true">
                <span class="mr-1">Supplier country</span>
              </header-with-tooltip>
            </div>
            <div class="mb-4 select-style">
              <model-list-select
                  :list="generateSortedSupplierCountryList"
                  :optionValue="'value'"
                  :optionText="'text'"
                  id="exceptionSubmissionRequestData.supplierCountry"
                  name="exceptionSubmissionRequestData.supplierCountry"
                  v-model="countryValue"
                  v-validate="'required'"
                  autocomplete="nope"
                  data-vv-validate-on="input"
                  @input="setCountryText"
              />
              <span
                  class="text-danger input-error-text"
                  v-if="errors.has('exceptionSubmissionRequestData.supplierCountry')"
              >This field is required</span>
            </div>
          </div>
        </div>

        <div class="mb-2">
          <div class="title my-4">
            <span>9. </span>
            <span class="mr-1">Please choose one of the following payment processes</span>
            
          </div>
          <div class="d-inline">
            <label class=" row col-12 label-font">
              <input
                  type="radio"
                  name="exceptionSubmissionRequestData.corporateCardType"
                  v-model="exceptionSubmissionRequestData.corporateCardType"
                  :value="'one-time'"
                  v-validate="'required'"
              />
              <div class="pl-1">This is a one-time engagement. Supplier will be paid via PO/invoice process.</div>                                         
              <header-with-tooltip
                :tooltip-text="CONSTANTS.CORPORATE_CARD_HELP_LINKS.TYPE1"
                :isHTML="true"
                :show-icon="true"> 
            </header-with-tooltip>
            </label
            >

            <label class=" row col-12 label-font">
              <input
                  type="radio"
                  name="exceptionSubmissionRequestData.corporateCardType"
                  v-model="exceptionSubmissionRequestData.corporateCardType"
                  :value="'credit-card'"
                  v-validate="'required'"
              />
                <div class="pl-1">Supplier must be paid via credit card. </div>
              <header-with-tooltip
                :tooltip-text="CONSTANTS.CORPORATE_CARD_HELP_LINKS.TYPE2"
                :isHTML="true"
                :show-icon="true"> 
            </header-with-tooltip>
              </label
            >

            <label class=" row col-12 label-font">
              <input
                  type="radio"
                  name="exceptionSubmissionRequestData.corporateCardType"
                  v-model="exceptionSubmissionRequestData.corporateCardType"
                  :value="'recurring-engagement'"
                  v-validate="'required'"
              />
              <div class="pl-1">This will be a recurring engagement w/Supplier. Standard supplier setup required. Supplier will be paid via PO/Invoice process.</div>
              <header-with-tooltip
                :tooltip-text="CONSTANTS.CORPORATE_CARD_HELP_LINKS.TYPE3"
                :isHTML="true"
                :show-icon="true"> 
            </header-with-tooltip>
             </label
            >
          </div>
          <show-errors
              :for-item="'exceptionSubmissionRequestData.corporateCardType'"
              :formErrors="errors"
              :field-name="exceptionSubmissionRequestData.corporateCardType" />
        </div>

        <div class="float-md-right mt-4 pr-0">
          <button class="btn-container flat-btn gray-btn close-btn btn mx-2 my-3" @click="navigateToHome">Cancel
          </button>
          <button class="btn-container btn flat-btn primary-blue-btn ml-2 my-3"
                  @click="validateAndSetCurrentTab('PreviewDetailsForExceptionRequest')">Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import supplierRequestMixin from "@/mixins/supplierRequestMixin";
import {ModelListSelect} from "vue-search-select"
import SupplierAutoComplete from "@/components/SupplierAutoComplete";
import HeaderWithTooltip from "@/components/HeaderWithTooltip";
import EXCEPTION_SUBMISSION_CONSTANTS from "@/store/exceptionSubmissionRequestFormConstants";
import exceptionRequestMixin from "@/mixins/exceptionRequestMixin";
import CONSTANTS from "@/store/constants";

export default {
  name: "SupplierInformationForExceptionSubmission",
  mixins: [supplierRequestMixin, exceptionRequestMixin],
  components: {ModelListSelect, SupplierAutoComplete, HeaderWithTooltip},
  data() {
    return {
      countryValue: '',
      supplierNameHelpText: 'Start typing the name of the supplier you want to work with. The system will provide suggestions from existing suppliers’ data. If you do not see your supplier suggested, type in the complete legal name and submit.',
      supplierAutoCompleteKey: 0,
      approximateCostTooltip: CONSTANTS.APPROXIMATE_SPEND_TOOLTIP
      }
  },
  created() {
    window.scrollTo(0, 0)
    this.setCountryValue()

  },
  mounted() {
    this.disableAutofill('exceptionSubmissionRequestData.supplierCountry')
  },
  computed: {
    ...mapGetters(['exceptionSubmissionRequestData', 'supplierContactRoleOptions', "adminDataList", "supplierCountryList"]),
    supplierName() {
      if (this.exceptionSubmissionRequestData.supplierName && this.$route.query.vendorId) {
        return this.exceptionSubmissionRequestData.supplierName
      } else {
        return "supplier"
      }
    },
    isCountryUS() {
      return this.exceptionSubmissionRequestData.companyBuyingServiceCheckBox
          && this.exceptionSubmissionRequestData.companyBuyingServiceCheckBox.some(countryObj =>
              countryObj.country === EXCEPTION_SUBMISSION_CONSTANTS.COUNTRY_US)
    },
    generateSortedSupplierCountryList() {
      return [...this.supplierCountryList].sort((a, b) => a.text.localeCompare(b.text))
    }
  },
  methods: {
    setCountryValue() {
      let country = this.supplierCountryList.find(country =>
          country.text === this.exceptionSubmissionRequestData.supplierCountry)
      this.countryValue = country ? country.value : ''
    },
    setCountryText(index) {
      this.exceptionSubmissionRequestData.supplierCountry = this.getTextValue(this.supplierCountryList, index)
    },
    assignSupplierName(event) {
      this.exceptionSubmissionRequestData.supplierName = event.filteredName
    },
    validateAndSetCurrentTab(tab) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$emit('setCurrentTab', tab)
        } else {
          window.scrollTo(0, 0)
        }
      })
    }
  }
}
</script>

<style scoped>
.preview-notification {
  margin-left: 0;
}


.border-outline {
  border: 1px solid rgb(196 199 202);
  padding: 15px;
  margin: 1px;
  color: #414953;
}

.border-text {
  margin-top: -28px;
  background: white;
  position: absolute;
}
</style>

<style scoped src="@/assets/stylesheets/vueSearchSelectOverrides.css">

</style>